<script module>
  export const i18n = {
    en: {
      planning: 'Planning',
      selection: 'Select Pass',
      tickets: 'Tickets',
      passengers: 'Passengers',
      order: 'Order',
      payment: 'Payment'
    },
    sv: {
      Change: 'Ändra',
      'Change language': 'Byt språk',
      'Change currency': 'Byt valuta',
      planning: 'Planering',
      selection: 'Välj pass',
      tickets: 'Biljetter',
      passengers: 'Passagerare',
      order: 'Beställ',
      payment: 'Betalning',
      'Main navigation': 'Huvudmeny'
    },
    da: {
      Change: 'Skift',
      'Change language': 'Skift sprog',
      'Change currency': 'Skift valuta',
      planning: 'Planlægning',
      selection: 'Valg pass',
      tickets: 'Billetter',
      passengers: 'Passagerer',
      order: 'Bestil',
      payment: 'Betaling',
      'Main navigation': 'Hovedmenu'
    }
  }

  export const LANGUAGES = {
    'en-gb': 'English',
    'sv-se': 'Svenska',
    'da-dk': 'Dansk',
    'es-es': 'Español',
    'fr-fr': 'Français',
    'de-de': 'Deutsch'
  }

  const CURRENCIES = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    AUD: '$',
    CAD: '$',
    SEK: 'kr',
    DKK: 'kr'
  }
</script>

<script>
  import { goto, afterNavigate } from '$app/navigation'
  import { browser } from '$app/environment'
  import { page } from '$app/state'

  import { prefixForLanguage } from '$lib/utils/prefix.js'
  import { resolve, resolveLang } from '$lib/prismic.js'
  import { register } from '$lib/utils/text.svelte.js'
  import State from '$lib/utils/state.svelte'
  import Button from '$lib/Button.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Logo from '$lib/Logo.svelte'

  /** @type {{
   * steps?: {name: string, selected?: boolean}[]?,
   * languages?: {href: string, lang: string}[]?,
   * adaptive?: boolean,
   * collapse?: boolean,
   * navigation?: boolean,
   * style?: string,
   * currencies?: boolean,
   * preserveBrand?: boolean,
   * primary?: Svelte.Snippet,
   * menu?: Svelte.Snippet
   * }} */
  let {
    steps,
    languages = [],
    adaptive = false,
    collapse = false,
    navigation = true,
    style = '',
    currencies = false,
    preserveBrand = false,
    primary,
    menu
  } = $props()

  let text = register(i18n)

  let open = $state(false)

  const toggle = (event) => {
    open = !open
    event.currentTarget.focus()
    event.preventDefault()
  }

  afterNavigate(() => {
    open = false
  })

  let prefix = $derived(
    State.language === page.data.settings?.lang.substring(0, 2)
      ? prefixForLanguage(
          State.language,
          page.data.agent?.settings?.data.default_language
        )
      : ''
  )

  let hasNavigation = $derived(navigation && page.data.settings)

  let languageOptions = $derived(
    languages?.map(({ href, lang }) => ({
      label: LANGUAGES[lang],
      value: href
    }))
  )
</script>

<header
  {style}
  class="header"
  class:steps
  class:adaptive
  class:collapse
  class:open
  class:navigation={hasNavigation}>
  <div class="content u-container">
    <div class="start">
      <div class="primary">{@render primary?.()}</div>
      {#if !preserveBrand && page.data.agent?.settings?.data.logotype.url}
        {@const href =
          page.data.agent.settings.data.website?.url || `${prefix}/`}
        <div class="primary">
          <a {href} rel="home" class="logo agent">
            <img
              class="logotype"
              src={page.data.agent.settings.data.logotype.url}
              alt={page.data.agent.settings.data.name} />
          </a>
        </div>
      {:else}
        <a
          href="{prefix}/"
          rel="home"
          class="primary"
          class:theme-blue={adaptive}>
          <div class="logo">
            <Logo horizontal />
          </div>
        </a>
      {/if}
    </div>

    {#if hasNavigation}
      <a href="#header-main" class="toggle" onclick={toggle}>Open</a>
    {/if}
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div
      class="main"
      id="header-main"
      aria-labelledby="menu-title"
      tabindex="0"
      role="dialog">
      {#if hasNavigation}
        <!-- svelte-ignore a11y_invalid_attribute -->
        <a href="#" class="toggle" onclick={toggle}>Close</a>
      {/if}

      <span class="u-hidden" id="menu-title">{text`Main navigation`}</span>

      <div class="middle">
        {#if steps}
          <ol class="steps">
            {#each steps as { name, selected }}
              <li class="step" class:selected>
                {text(name)}
              </li>
            {/each}
          </ol>
        {/if}

        {#if hasNavigation}
          <ul class="nav">
            {#each page.data.settings?.data.body as slice}
              {@const { slice_type, primary, items } = slice}
              {#if slice_type === 'link_group'}
                <li class="group">
                  <a
                    class="link title"
                    target={primary.link.target || null}
                    href={resolve(primary.link)}>
                    {primary.label}
                  </a>
                  <ul class="sub">
                    {#each items as { label, link, description, badge }}
                      {@const href = resolve(link)}
                      <li>
                        <a class="link" {href} target={link.target || null}>
                          {label}
                          {#if badge}
                            <span class="pill">{badge}</span>
                          {/if}
                          {#if description}
                            <br />
                            <span class="hint">{description}</span>
                          {/if}
                        </a>
                      </li>
                    {/each}
                  </ul>
                </li>
              {/if}
            {/each}
          </ul>

          {#if menu}
            <div>
              {@render menu()}
            </div>
          {/if}
        {/if}
      </div>

      {#if hasNavigation}
        <div class="end">
          {#if (languages?.length && languages.length > 1) || currencies}
            <div class="aside">
              <form class="form">
                {#if languages?.length}
                  <label class="field language" for="app-language">
                    <span class="u-hidden">{text`Change language`}</span>
                    <select
                      id="app-language"
                      class="select language"
                      name="location"
                      required
                      title={text`Change language`}
                      onchange={(event) => {
                        const { value } = event.currentTarget
                        const href =
                          `${value}?${page.url.searchParams}`.replace(
                            /\/?\??$/,
                            ''
                          )
                        goto(href || '/', {
                          replaceState: true,
                          noScroll: true
                        })
                      }}>
                      <option
                        class="option"
                        disabled
                        value=""
                        selected={!browser}>
                        {text(resolveLang(State.language))}
                      </option>
                      {#if languageOptions}
                        {#each languageOptions as { value, label }}
                          {@const selected =
                            browser && value === page.url.pathname}
                          <option class="option" {value} {selected}>
                            {label}
                          </option>
                        {/each}
                      {/if}
                    </select>
                    <Symbol name="globe" />
                  </label>
                {/if}
                {#if currencies}
                  <label class="field currency" for="app-currency">
                    <span class="u-hidden">{text`Change currency`}</span>
                    <select
                      id="app-currency"
                      class="select currency"
                      name="currency"
                      value={State.currency}
                      required
                      title={text`Change currency`}
                      onchange={(event) => {
                        State.currency = event.currentTarget.value
                      }}>
                      <option
                        class="option"
                        disabled
                        value=""
                        selected={!browser}>
                        {CURRENCIES[State.currency]}
                        {State.currency}
                      </option>
                      {#each Object.entries(CURRENCIES) as [value, label]}
                        {@const selected = browser && value === State.currency}
                        <option class="option" {value} {selected}>
                          {label}
                          {value}
                        </option>
                      {/each}
                    </select>
                  </label>
                {/if}
                {#if !browser}
                  <span class="submit">
                    <Button formnovalidate size="xs">{text`Change`}</Button>
                  </span>
                {/if}
                {#each Array.from(page.url.searchParams) as [key, value]}
                  <input type="hidden" name={key} {value} />
                {/each}
              </form>
            </div>
          {:else}
            {#each page.data.settings?.data.body as slice}
              {@const { slice_type, primary, items } = slice}
              {#if slice_type === 'action_button'}
                <div class="aside">
                  <Button size="sm" href={resolve(primary.link)}>
                    {primary.label}
                  </Button>
                  <ul>
                    {#each items as { label, link }}
                      {@const href = resolve(link)}
                      <li>
                        <a class="link small" {href}>{label}</a>
                      </li>
                    {/each}
                  </ul>
                </div>
              {/if}
            {/each}
          {/if}
        </div>
      {/if}
    </div>
  </div>
</header>

<style>
  .header {
    width: 100%;
    font-weight: 600;
    user-select: none;
    color: rgb(var(--rgb-text));
    font-size: 1.125rem;
    position: relative;
    z-index: 2;
    background: var(--current-background);
  }

  @media (width > 900px) {
    .header {
      font-size: 1.15rem;
    }
  }

  @media (width < 1000px) and (height < 700px) {
    .header {
      z-index: 2;
    }
  }

  .adaptive {
    height: 0;
    background: transparent;
    color: rgb(var(--rgb-white));
  }

  .content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1rem;
    padding-bottom: 1rem;
    position: relative;
  }

  @media (width > 500px) {
    .header .content {
      padding-top: 1.75rem;
      padding-bottom: 1.75rem;
    }
  }

  @media (width > 900px) {
    .header.steps .content {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      padding-top: 1.875rem;
      padding-bottom: 1.875rem;
    }
  }

  /* Mobile/desktop layouts */

  .main {
    position: fixed;
    width: 1px;
    height: 1px;
    top: 0;
    left: 0;
    background: var(--background);
    overscroll-behavior: contain;
    overflow: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  :root:has(.header.open) {
    overflow: hidden;
  }

  @media (width < 901px) {
    .open .main,
    .main:focus,
    .main:has(.link:focus) {
      width: 100%;
      height: 100svh;
      padding: 0 var(--document-margin);
      color: rgb(var(--rgb-text));
    }
  }

  .middle {
    flex: 1;
    padding-top: 15svh;
  }

  .aside {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 1.5rem 0;
  }

  .aside ul {
    display: flex;
    gap: 0.75rem;
  }

  .nav {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .group {
    line-height: 1.4;
  }

  @media (width > 900px) {
    .start,
    .end {
      flex-basis: 15rem;
    }

    .end {
      display: flex;
      justify-content: flex-end;
    }

    .main,
    .open .main,
    .main:focus,
    .main:has(.link:focus) {
      position: relative;
      background: none;
      width: auto;
      height: auto;
      padding: 0;
      flex: 1;
      flex-direction: row;
      align-items: center;
      overflow: visible;
    }

    .middle {
      position: relative;
      background: none;
      width: auto;
      height: auto;
      padding: 0;
    }

    .nav {
      flex-direction: row;
      justify-content: center;
      gap: 2rem;
    }

    .aside {
      padding: 0;
      gap: 1.25rem;
      justify-content: flex-end;
    }

    .aside ul {
      display: none;
      order: -1;
      gap: 1.25rem;
    }
  }

  @media (width > 1200px) {
    .start,
    .end {
      flex-basis: 20rem;
    }

    .aside ul {
      display: flex;
    }
  }

  /* Toggle */

  .toggle {
    position: absolute;
    right: calc(var(--document-margin) - 0.5rem);
    top: 0.9rem;
    font-size: 0;
    color: rgb(var(--rgb-blue));
    width: 3.25rem;
    height: 2.6rem;
    padding: 1rem;
  }

  .adaptive .toggle {
    color: inherit;
  }

  .toggle::before,
  .toggle::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 1.25rem;
    border-radius: 1rem;
    left: 1rem;
    top: 1rem;
    background: currentColor;
    transform-origin: center center;
    transition: transform 90ms var(--ease-out);
  }

  .toggle::after {
    top: auto;
    bottom: 1rem;
  }

  .open .toggle {
    position: fixed;
  }

  .open .toggle::before {
    transform: translateY(calc(0.3rem - 1px)) rotate(45deg);
  }

  .open .toggle::after {
    transform: translateY(calc(-0.3rem + 1px)) rotate(-45deg);
  }

  @media (width > 500px) {
    .toggle {
      top: 1.4rem;
    }
  }

  @media (width > 600px) {
    .toggle {
      top: 1.8rem;
    }
  }

  @media (width > 900px) {
    .toggle {
      display: none;
    }
  }

  /* Logotype */

  .primary {
    display: flex;
    align-items: center;
    margin-right: auto;
  }

  @media (width < 901px) {
    .open .primary,
    .header:has(.main:focus) .primary,
    .header:has(.link:focus) .primary {
      --logo-1: var(--rgb-blue);
      --logo-2: var(--rgb-yellow);
      --logo-3: var(--rgb-red);

      position: relative;
      z-index: 1;
    }
  }

  .primary:empty {
    display: none;
  }

  .primary:not(:empty) + .primary {
    display: none;
  }

  .logotype {
    display: block;
    width: auto;
    height: 100%;
  }

  .logo {
    display: block;
    height: 1.75rem;
    position: relative;
    overflow: hidden;
  }

  .logo.agent {
    height: 2.5rem;
  }

  .header.steps .logo {
    height: 1.5rem;
  }

  .header.steps .logo.agent {
    height: 2.5rem;
  }

  @media (width > 500px) {
    .header.steps .logo {
      height: 2rem;
    }

    .header.steps .logo.agent {
      height: 3rem;
    }
  }

  .navigation .logo {
    height: 2rem;
  }

  .navigation .logo.agent {
    height: 2.5rem;
  }

  @media (width > 600px) {
    .logo {
      height: 2.25rem;
    }

    .navigation .logo.logo {
      width: auto;
      height: 2.5rem;
    }
  }

  @media (width > 700px) {
    .navigation .logo.agent {
      height: 3rem;
    }
  }

  @media (width > 900px) {
    .navigation .logo.agent {
      height: 5rem;
    }
  }

  @media (width > 900px) {
    .header .content {
      padding: 2.5rem var(--document-margin);
    }
  }

  /* Steps */

  .header .steps {
    display: flex;
    justify-self: center;
    align-items: center;
    font-size: 1rem;
    justify-content: center;
  }

  @media (width < 900px) {
    .header .steps {
      display: none;
    }
  }

  .step {
    display: flex;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
  }

  .step:not(:last-child)::after {
    content: '';
    width: 1rem;
    height: 1rem;
    margin: 0 0.5rem;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' alt='' role='presentation' width='6' height='6'><circle cx='0.8' cy='0.8' r='0.8' fill='rgb(127, 133, 159, 0.6)' /></svg>");
    background-position: 0 65%;
    background-repeat: repeat-x;
  }

  @media (width > 1000px) {
    .step:not(:last-child)::after {
      width: 2rem;
    }
  }

  .step::before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    margin-right: 0.5rem;
    border-radius: 100%;
    background: rgba(var(--rgb-muted), 0.75);
    top: -1px;
    position: relative;
  }

  .step.selected::before {
    background: rgb(var(--rgb-highlight));
  }

  .step:not(.selected) {
    font-weight: normal;
    color: rgba(var(--rgb-muted), 0.75);
  }

  /* Form */

  .form {
    display: flex;
    justify-self: flex-end;
    align-items: center;
    gap: clamp(0.3rem, 1vw, 1.25rem);
  }

  .field {
    display: flex;
    align-items: center;
    position: relative;
  }

  .field.language {
    padding: 0.25rem;
  }

  .select.language {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border: 0;
    color: transparent;
    background: none;
    appearance: none;
  }

  .select.currency {
    border: 0;
    font-size: 1rem;
    line-height: 1;
    font-weight: bold;
    appearance: none;
    color: currentColor;
    border-radius: var(--border-radius-xs);
    padding: 0.45rem 0.2rem 0.45rem 0.5rem;
    letter-spacing: -0.02em;
    background: rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }

  .adaptive .select.currency {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (width > 900px) {
    .select.currency {
      font-size: 1.05rem;
    }
  }

  .submit {
    display: none;
    position: relative;
  }

  .form:has(select:valid) .submit {
    display: inline;
  }

  .option:disabled {
    display: none;
  }

  /* Navigation */

  .header a {
    transition:
      opacity 300ms var(--ease-out),
      var(--color-transition);
  }

  .header a:active {
    opacity: 0.7;
    transition: opacity 40ms var(--ease-out);
  }

  .header a:focus-visible {
    outline-offset: calc(var(--focus-ring-width) * -1);
  }

  .link {
    font-size: 1rem;
    font-weight: 500;
    text-wrap: balance;
    white-space: nowrap;
  }

  .link.title {
    font-size: 1.5rem;
    font-weight: bold;
    font-family: var(--heading-font-family);
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
  }

  .link.small {
    font-weight: 500;
    font-size: 0.875rem;
  }

  .hint {
    display: none;
  }

  .pill {
    font-size: 0.6875rem;
    text-transform: uppercase;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: rgb(var(--rgb-green));
    background: rgba(var(--rgb-green), 0.2);
    border-radius: 1rem;
    padding: 0.15rem 0.35rem;
    position: relative;
    top: -0.05rem;
    margin-left: 0.3rem;
  }

  @media (width > 900px) {
    .link,
    .link.title {
      font-weight: 400;
      display: block;
    }

    .sub .link:hover {
      color: rgb(var(--rgb-link));
    }

    .link.title {
      font-weight: 550;
      font-size: 1rem;
      font-family: inherit;
      word-spacing: inherit;
      letter-spacing: inherit;
      display: flex;
      align-items: center;
      gap: 0.45rem;
    }

    .link:not(.title) {
      font-weight: 600;
    }

    .link.title:has(+ .sub)::after {
      content: '';
      width: 0.45rem;
      height: 0.45rem;
      border: solid rgb(var(--rgb-muted));
      border-width: 1.5px 1.5px 0 0;
      transform: rotate(135deg);
      position: relative;
      top: -0.1rem;
      transition:
        border-color 200ms var(--ease-out),
        opacity 200ms var(--ease-out);
    }

    .adaptive .link.title:has(+ .sub)::after {
      border-color: inherit;
      opacity: 0.5;
    }

    .nav .sub {
      position: relative;
    }

    .nav .sub li {
      line-height: 1.15;
    }

    .nav .sub li + li {
      border-top: 0.5px solid rgba(var(--rgb-gray), 0.35);
      margin-top: 0.9rem;
      padding-top: 0.8rem;
    }

    .link.title + .sub {
      position: absolute;
      transition:
        opacity 250ms 100ms cubic-bezier(0.22, 1, 0.36, 1),
        transform 250ms 100ms cubic-bezier(0.22, 1, 0.36, 1);
      opacity: 0;
      height: 0;
      top: -50rem;
    }

    .link.title:hover + .sub,
    .link.title + .sub:hover,
    .link.title:focus + .sub,
    .link.title + .sub:has(a:focus) {
      opacity: 1;
      top: 2.5rem;
      height: auto;
    }

    .link.title:hover::after,
    .link.title:has(+ .sub:hover)::after {
      border-color: currentColor;
      opacity: 1 !important;
    }

    .link.title + .sub {
      color: rgb(var(--rgb-text));
      background: var(--background);
      border-radius: var(--border-radius-md);
      box-shadow: var(--box-shadow-lg);
      padding: 1.5rem 1.25rem;
      width: 100%;
      max-width: 26rem;
      left: 50%;
      transform: translateX(-50%);
      border: 0.5px solid rgba(var(--rgb-gray), 0.35);
    }

    .link.title + .sub::before {
      content: '';
      width: 100%;
      height: 1rem;
      top: -1rem;
      left: 0;
      display: block;
      position: absolute;
    }

    .link .hint,
    .link.small {
      font-weight: 400;
      font-size: 1rem;
      color: rgb(var(--rgb-muted));
    }

    .adaptive .link.small {
      color: currentColor;
    }

    .link .hint {
      text-wrap: pretty;
      line-height: 1.3;
      display: block;
      font-size: 0.9375rem;
      padding-top: 0.25rem;
      white-space: normal;
      text-wrap: balance;
    }
  }

  @media (width > 1000px) {
    .link.title {
      font-size: 1.125rem;
    }
  }
</style>
